import type { ReactNode } from "react";

/**
 * S1 报告 v2 共用件。
 *
 * ProvenanceBadge:实测/推演徽标 —— 判定**唯一真源是 provenance 字段**,
 * 禁止从平台名/文案猜(变异验证锁:改成猜文案测试必红)。
 */

/** 徽标释义(2026-07-29:测试反馈「模型提及概览中的推演是什么意思?」—— 界面一个字没解释)。 */
export const PROVENANCE_HINT: Record<string, string> = {
  real: "联网实测:该平台真的被提问并现场作答,逐条留了证据(引用 URL / HTTP 状态 / 耗时 / 模型 / 采集时间)。",
  derived:
    "生态推演:该平台目前没有可用的 API 通道,数值是以实测平台的结果为参照、按该平台的内容生态确定性推算出来的,不是真实问答 —— 所以没有原话样本,引用也只到域名层。同一家企业重跑结果一致。",
};

export function ProvenanceBadge({ provenance }: { provenance?: string }) {
  if (provenance === "real") {
    return (
      <span
        data-testid="geo-prov-badge"
        data-provenance="real"
        title={PROVENANCE_HINT.real}
        className="inline-flex cursor-help items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-2xs font-bold text-emerald-600"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        实测
      </span>
    );
  }
  if (provenance === "derived") {
    return (
      <span
        data-testid="geo-prov-badge"
        data-provenance="derived"
        title={PROVENANCE_HINT.derived}
        className="inline-flex cursor-help items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-2xs font-bold text-amber-600"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        推演
      </span>
    );
  }
  return null;
}

/** 12 板块 section 头(编号 + 标题 + 说明,照 dept-radar-report 版式)。 */
export function SecNo({ no, title, desc }: { no: string; title: string; desc?: string }) {
  return (
    <div className="flex items-baseline gap-2.5 pt-2">
      <span className="font-mono text-xs font-extrabold tracking-wider text-primary">{no}</span>
      <h2 className="text-base font-extrabold">{title}</h2>
      {desc && <span className="text-xs text-muted-foreground">{desc}</span>}
    </div>
  );
}

/** KPI 小卡(核心摘要/搜索概览/平台详情共用)。 */
export function Kpi({
  value,
  label,
  negative,
}: {
  value: ReactNode;
  label: string;
  negative?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-background px-3 py-2.5">
      <b className={`block text-lg font-extrabold tabular-nums ${negative ? "text-rose-500" : ""}`}>
        {value}
      </b>
      <span className="text-2xs text-muted-foreground">{label}</span>
    </div>
  );
}

/** 命中状态 pill(问询行/受众表现共用)。 */
export function HitPill({
  kind,
  children,
}: {
  kind: "rec" | "hit" | "no" | "bad";
  children: ReactNode;
}) {
  const cls =
    kind === "rec"
      ? "bg-primary/10 text-primary"
      : kind === "hit"
        ? "bg-emerald-500/15 text-emerald-600"
        : kind === "bad"
          ? "bg-rose-500/12 text-rose-500"
          : "bg-muted text-muted-foreground";
  return (
    <span className={`inline-block flex-none rounded-full px-2 py-0.5 text-2xs font-bold ${cls}`}>
      {children}
    </span>
  );
}

/** 问询行的命中态(pill 语义与 mock 对齐:进推荐名单/提及/未提及/负向)。 */
export function rowPill(row: { mentioned: boolean; sentiment: string; is_recommended: boolean }): {
  kind: "rec" | "hit" | "no" | "bad";
  text: string;
} {
  if (row.sentiment === "negative") return { kind: "bad", text: "负向" };
  if (row.is_recommended) return { kind: "rec", text: "进推荐名单" };
  if (row.mentioned) return { kind: "hit", text: "提及" };
  return { kind: "no", text: "未提及" };
}
